/**
 * Adapter: arena map → the shared nav data layer. The arena is the first consumer of
 * the one navigation system; the world (farm) gets its own adapter later, feeding the
 * SAME buildNavGrid/fields — never a parallel implementation.
 */

import { Tile, tileCost, type ArenaMap, inSafeSpace } from '../arena/map';
import { buildNavGrid, tileIndex, NavFlag, type NavGrid, type TileSource } from './grid';
import { dijkstraField, markChokepoints, markCover, opennessMap } from './fields';

function arenaSource(map: ArenaMap): TileSource {
  return {
    w: map.w, h: map.h,
    blocked: (x, y) => !Number.isFinite(tileCost(map.tiles[tileIndex(x, y, map.w)] as Tile)),
    cost: (x, y) => tileCost(map.tiles[tileIndex(x, y, map.w)] as Tile),
    flags: (x, y) => {
      const t = map.tiles[tileIndex(x, y, map.w)] as Tile;
      let f = 0;
      if (t === Tile.Bridge) f |= NavFlag.BRIDGE;
      return f;
    },
  };
}

export interface ArenaNav {
  grid: NavGrid;
  /** Dijkstra cost to the central safe space from every tile (retreat lookup). */
  distToSafe: Float64Array;
  chokepoints: number[];
  openness: Float32Array;
}

/** Build the full nav data layer for an arena match, in one pass. */
export function buildArenaNav(map: ArenaMap): ArenaNav {
  const grid = buildNavGrid(arenaSource(map));
  markChokepoints(grid);
  markCover(grid);
  // Safe-space tiles are the retreat sources.
  const safe: number[] = [];
  for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
    if (inSafeSpace(map, x, y) && grid.walkable[tileIndex(x, y, map.w)]) safe.push(tileIndex(x, y, map.w));
  }
  return {
    grid,
    distToSafe: dijkstraField(grid, safe),
    chokepoints: grid.flags.reduce<number[]>((acc, f, i) => { if (f & NavFlag.CHOKEPOINT) acc.push(i); return acc; }, []),
    openness: opennessMap(grid),
  };
}
